import React, { useState, useEffect } from 'react';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import HardDrive from 'lucide-react/dist/esm/icons/hard-drive';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Folder from 'lucide-react/dist/esm/icons/folder';
import { myFilesService } from '../../lib/myFilesService';

export function FileStats() {
  const [stats, setStats] = useState<{
    totalFiles: number;
    totalSize: number;
    byCategory: Record<string, number>;
    byFolder: Record<string, number>;
  } | null>(null);
  const [storageUsage, setStorageUsage] = useState<{
    totalUsed: number;
    maxStorage: number;
    percentageUsed: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [fileStats, usage] = await Promise.all([
        myFilesService.getFileStats(),
        myFilesService.getStorageUsage()
      ]);
      setStats(fileStats);
      setStorageUsage(usage);
    } catch (error) {
      console.error('Failed to load file stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      custom: '#abd3d2',
      legal: '#172241',
      financial: '#4ade80',
      health: '#ef4444',
      pets: '#f97316',
      travel: '#3b82f6',
      digital: '#8b5cf6',
      legacy: '#facc15',
      ndis: '#06b6d4'
    };
    return colors[category] || '#abd3d2';
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2 mt-2"></div>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="h-3 bg-gray-200 rounded w-1/3 mt-2"></div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-sm text-gray-500">
        Unable to load statistics
      </div>
    );
  }

  const topCategories = Object.entries(stats.byCategory)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  return (
    <div className="space-y-4">
      {/* Total Files and Size */}
      <div className="grid grid-cols-2 gap-3">
        <div className="text-center p-2 bg-idecide-primary bg-opacity-10 rounded-lg">
          <div className="flex items-center justify-center mb-1">
            <FileText size={16} className="text-idecide-primary" />
          </div>
          <div className="text-lg font-semibold text-idecide-dark">
            {stats.totalFiles}
          </div>
          <div className="text-xs text-gray-600">Files</div>
        </div>
        
        <div className="text-center p-2 bg-idecide-primary bg-opacity-10 rounded-lg">
          <div className="flex items-center justify-center mb-1">
            <HardDrive size={16} className="text-idecide-primary" />
          </div>
          <div className="text-lg font-semibold text-idecide-dark">
            {formatFileSize(stats.totalSize)}
          </div>
          <div className="text-xs text-gray-600">Storage</div>
        </div>
      </div>

      {/* Top Categories */}
      {topCategories.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Top Categories</h4>
          <div className="space-y-2">
            {topCategories.map(([category, count]) => (
              <div key={category} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getCategoryColor(category) }}
                  />
                  <span className="text-sm text-gray-600 capitalize">
                    {category}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Storage Usage Bar */}
      {storageUsage && (
        <div>
          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>Storage Usage</span>
            <span>{formatFileSize(storageUsage.totalUsed)} / {formatFileSize(storageUsage.maxStorage)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                storageUsage.percentageUsed > 90 ? 'bg-red-500' : 
                storageUsage.percentageUsed > 75 ? 'bg-yellow-500' : 'bg-idecide-primary'
              }`}
              style={{ 
                width: `${Math.min(storageUsage.percentageUsed, 100)}%`
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {storageUsage.percentageUsed.toFixed(1)}% of 15GB used
          </p>
        </div>
      )}
    </div>
  );
} 